import { unrefElement, useEventListener, useNow } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { reportClientError } from "#ce/utils/error-report";
import { defineStore } from "#ce/utils/store";
import { createPurchaseApi } from "#ce/components/SpCheckout/api/purchase";
import { prepareEligibilityValidation } from "#ce/components/SpCheckout/utils/form";

const KEY_FILTER = "registration[fields]";
/** Each match is one `[segment]` in a nested form key; capture group 1 is the segment name. */
const BRACKETED_FORM_KEY_SEGMENT_REGEX = /\[([^\]]+)\]/g;
const ONE_MINUTE = 60;

/** Maps `registration[fields]…` FormData keys to flat review keys (e.g. `address[street]`). */
export function registrationFieldKeyFromFormKey(key) {
  if (!key.startsWith(KEY_FILTER)) {
    return null;
  }

  const remainder = key.slice(KEY_FILTER.length);
  const segments = [...remainder.matchAll(BRACKETED_FORM_KEY_SEGMENT_REGEX)].map((m) => m[1]);

  if (!segments.length) {
    return null;
  }

  return (
    segments[0] +
    segments
      .slice(1)
      .map((s) => `[${s}]`)
      .join("")
  );
}

const paymentTypes = {
  card: {
    name: "Credit Card",
  },
  paypal: {
    name: "Paypal",
  },
};

export const usePurchaseStore = (options = {}) =>
  defineStore("purchase", () => {
    const api = options?.api ?? createPurchaseApi();

    const provider = ref();
    const organizationName = ref();
    const purchaseToken = ref();
    const environment = ref();
    const publishableKey = ref();
    const clientSecret = ref();
    const captureUrl = ref();
    const returnUrl = ref();
    const lineItems = ref();
    const expiresAt = ref();

    const extendExpirationUrl = ref();
    const applyPromoCodeUrl = ref();
    const removePromoCodeUrl = ref();

    const paymentEnabled = ref(false);
    const paymentRequired = ref(false);
    const paymentElement = ref();
    const aboutToConfirmPayment = ref(false);

    const paymentType = ref();
    const paymentName = ref();

    const totalAmount = ref(0);
    const discountName = ref();
    const discountAmount = ref(0);
    const promoCode = ref();

    const entries = ref();
    const formElement = ref();
    const formResult = ref();
    const registrationUrl = ref();
    const formFields = ref();

    /**
     * The resource object provided as a prop to the custom element.
     * It represents the resource that is being purchased.
     * It is used to determine the type of payment and render the correct panels.
     *
     * @param {import("vue").Ref<Object>} resource
     */
    const resource = ref();

    function setResource(value) {
      resource.value = value;
    }

    watch(paymentElement, (element) => {
      useEventListener(element, "update-payment-type", ({ detail }) => {
        const [type] = detail;
        paymentType.value = type;
      });
    });

    watch(paymentEnabled, (enabled) => {
      paymentRequired.value = enabled;
    });

    watch(paymentType, (value) => {
      if (value) {
        const { name } = paymentTypes[value] ?? {};
        paymentName.value = name;
      }
    });

    function validatePayment() {
      return paymentElement.value.validate();
    }

    async function confirmPayment() {
      aboutToConfirmPayment.value = true;
      const result = await paymentElement.value.confirmPayment();
      aboutToConfirmPayment.value = false;

      return result;
    }

    function createEntries(formData) {
      entries.value = Array.from(formData.keys()).reduce((acc, key) => {
        const nestedKey = registrationFieldKeyFromFormKey(key);

        if (nestedKey) {
          acc[nestedKey] = formData.get(key);
        }

        return acc;
      }, {});
    }

    function isValid() {
      prepareEligibilityValidation(formElement.value);

      formElement.value.reportValidity();
      return formElement.value.checkValidity();
    }

    const isAddingToCart = ref(false);
    let reportedDuplicateAdd = false;

    // Returns `true` on success, `false` on a failed request, and `null` when the call was skipped
    // because one was already in flight or this cart already has a purchase. Callers rely on the
    // explicit `false` to surface a retryable error instead of spinning forever.
    async function addToCart(url) {
      // Prevent multiple simultaneous calls
      if (isAddingToCart.value) {
        return null;
      }

      // This cart already has a purchase. Adding again mints a second Purchase and a second payment
      // intent, while the payment form stays mounted against the first one — which is how a
      // registrant ends up charged for a purchase their registration is not attached to.
      //
      // The url is deliberately not part of the condition: a checkout is bound to a single event and
      // a purchase has only ever held one item, so a differing url means a duplicate call rather
      // than a second thing to buy. Comparing urls would let a duplicate through whenever the
      // caller varied the string at all.
      if (purchaseToken.value) {
        reportDuplicateAdd(url);
        return null;
      }

      isAddingToCart.value = true;

      try {
        const response = await api.addToCart(url);

        // A 2xx payload without a token would leave `hasPurchaseToken` false and the
        // loading overlay spinning with no retry — treat it as a failed request.
        if (!response?.purchase_token) {
          // Report the response *shape*, never its values — a well-formed payload carries
          // secrets (client_secret, publishable_key), so serializing the body would leak them.
          const shape = response && typeof response === "object" ? Object.keys(response).join(", ") : typeof response;
          reportClientError("Event registration checkout received no purchase token", {
            description: `${url} — 2xx response with no purchase_token (keys: ${shape})`,
            baseUrl: url,
          });
          return false;
        }

        purchaseToken.value = response.purchase_token;
        clientSecret.value = response.client_secret;
        captureUrl.value = response.capture_url;
        returnUrl.value = response.return_url;
        lineItems.value = response.line_items ?? [];
        expiresAt.value = new Date(response.expires_at).getTime();
        extendExpirationUrl.value = response.extend_expiration_url;
        applyPromoCodeUrl.value = response.apply_code_url ?? response.apply_promo_url;
        removePromoCodeUrl.value = response.remove_code_url ?? response.remove_promo_url;
        formFields.value = response.form_fields;
        registrationUrl.value = response.registration_url;

        provider.value = response.provider;
        environment.value = response.environment;
        publishableKey.value = response.publishable_key;
        organizationName.value = response.organization_name;

        updatePaymentValues(response);

        return true;
      } catch (err) {
        reportClientError("Event registration checkout failed to add to cart", {
          description: `${url} — ${err}`,
          baseUrl: url,
        });
        return false;
      } finally {
        isAddingToCart.value = false;
      }
    }

    // What re-triggers add-to-cart has not been identified from the code, so the skip is reported to
    // surface it from real traffic — the report carries the page URL that should show the cause.
    // Reported once per checkout: roughly a quarter of purchases sit in a duplicate chain, so
    // reporting every skip would bury the signal in its own volume.
    function reportDuplicateAdd(url) {
      if (reportedDuplicateAdd) {
        return;
      }

      reportedDuplicateAdd = true;

      reportClientError("Event registration checkout skipped a duplicate add-to-cart", {
        description: `${url} — a purchase already exists for this cart`,
        baseUrl: url,
      });
    }

    /**
     * A flattened array of form fields, with the name transformed to a string with brackets
     * @type {import("vue").ComputedRef<Array<{ name: string, value: any }>>}
     */
    const formFieldsFlattened = computed(() => {
      const mapper = ({ name, value }) => ({ name: `[fields]${name}`, value });
      return flattenFormFields(formFields.value ?? {}).map(mapper);
    });

    /**
     * convert a deeply nested object to a flat array of the form [{ name: 'name', value: 'value' }]
     * the name is the path to the value in the object, transformed to a string with brackets
     * e.g. { address: { city: 'Berlin' } } => [{ name: '[address][city]', value: 'Berlin' }]
     *
     * @private
     * @param {Object} fields
     * @returns {Array<{ name: string, value: any }>}
     */
    function flattenFormFields(fields) {
      return Object.entries(fields).reduce((acc, [key, value]) => {
        if (typeof value === "object") {
          const mapper = ({ name, value }) => ({ name: `[${key}]${name}`, value });
          return [...acc, ...flattenFormFields(value).map(mapper)];
        }

        return [...acc, { name: `[${key}]`, value }];
      }, []);
    }

    const nowDate = useNow();
    const now = computed(() => nowDate.value.getTime());

    const isExpired = computed(() => {
      if (!expiresAt.value) {
        return false;
      }

      return now.value > expiresAt.value;
    });

    const secondsUntilExpiration = computed(() => {
      if (isExpired.value) {
        return 0;
      }
      return Math.floor((expiresAt.value - now.value) / 1000);
    });

    const expiresWithinSeconds = computed(() => expiresAt.value && secondsUntilExpiration.value <= ONE_MINUTE);

    async function extendExpiration() {
      const response = await api.extendExpiration(extendExpirationUrl.value);
      expiresAt.value = new Date(response.expires_at).getTime();
    }

    async function submitForm() {
      const formData = new FormData(formElement.value);
      createEntries(formData);

      formData.append("purchase_token", purchaseToken.value);
      formData.append("embedded_at_url", window.location.href);

      try {
        if (!registrationUrl.value) {
          formResult.value = await api.submitForm(formElement.value.action, formData);
          registrationUrl.value = formResult.value.url;
        } else {
          formResult.value = await api.updateForm(registrationUrl.value, formData);
        }
        updatePaymentValues(formResult.value);

        return true;
      } catch {
        return null;
      }
    }

    async function applyPromoCode(code) {
      discountAmount.value = 0;

      try {
        const response = handlePromoApiResponse(await api.applyPromoCode(applyPromoCodeUrl.value, code));
        promoCode.value = code;
        return response;
      } catch (err) {
        reportPromoCodeFailure("apply", applyPromoCodeUrl.value, err);
        return handlePromoApiResponseError(err, "Unable to apply promo code");
      }
    }

    async function removePromoCode() {
      try {
        const response = handlePromoApiResponse(await api.removePromoCode(removePromoCodeUrl.value));
        promoCode.value = undefined;
        return response;
      } catch (err) {
        reportPromoCodeFailure("remove", removePromoCodeUrl.value, err);
        return handlePromoApiResponseError(err, "Unable to remove promo code");
      }
    }

    function handlePromoApiResponse(response) {
      updatePaymentValues(response);

      return { error: undefined };
    }

    function handlePromoApiResponseError(err, message) {
      return { error: err?.data?.error ?? message };
    }

    // A promo-code request that reaches the server and comes back a 4xx is an expected
    // business rejection (invalid/expired code) — high volume, not actionable, kept out
    // of Honeybadger. A transport failure (network/DNS/timeout has no HTTP status) or a
    // 5xx is an infrastructure problem worth tracking — the case where the user gets a
    // generic "Unable to apply promo code" with no server-side trace otherwise.
    function reportPromoCodeFailure(action, url, err) {
      if (err?.status && err.status < 500) {
        return;
      }

      reportClientError(`Event registration checkout failed to ${action} promo code`, {
        description: `${url} — ${err}`,
        baseUrl: url,
      });
    }

    function updatePaymentValues(values) {
      const { payment_required, payment_amount, discount_name, discount_amount } = values ?? {};

      paymentRequired.value = payment_required;
      totalAmount.value = payment_amount;
      discountName.value = discount_name;
      discountAmount.value = discount_amount;
    }

    function reset() {
      purchaseToken.value = undefined;
      reportedDuplicateAdd = false;
      formResult.value = undefined;
      formElement.value?.reset();
      formFields.value = undefined;
      expiresAt.value = undefined;
    }

    async function declineRsvp(url) {
      return api.declineRsvp(url);
    }

    return {
      provider,

      organizationName,
      environment,

      entries,
      resource,

      setFormElement: (element) => (formElement.value = element),

      get formElement() {
        return formElement.value;
      },

      get formResult() {
        return formResult.value;
      },

      formFields,
      formFieldsFlattened,

      isValid,
      addToCart,
      submitForm,
      applyPromoCode,
      removePromoCode,
      reset,

      purchaseToken,
      publishableKey,
      clientSecret,
      lineItems,
      captureUrl,
      returnUrl,

      expiresAt,
      isExpired,
      expiresWithinSeconds,
      secondsUntilExpiration,
      extendExpiration,

      paymentEnabled,
      paymentType,
      paymentName,

      paymentRequired,
      totalAmount,
      discountName,
      discountAmount,
      promoCode,

      setResource,

      setPaymentEnabled: (enabled) => (paymentEnabled.value = enabled),
      setPaymentElement: (element) => (paymentElement.value = unrefElement(element)),

      validatePayment,
      confirmPayment,
      aboutToConfirmPayment,

      declineRsvp,

      get hasPurchaseToken() {
        return purchaseToken.value !== undefined;
      },

      get hasPaymentData() {
        return Boolean(paymentType.value) || Boolean(paymentRequired.value);
      },

      get hasDiscount() {
        return discountAmount.value;
      },
    };
  })();
